import { z } from "zod";
import {
  bookingModelSchema,
  bookingSourceSchema,
  bookingStatusSchema,
  freeCancellationModeSchema,
} from "../enums.ts";
import { policyOutcomeSchema } from "../policy/types.ts";
import { isoDateTimeSchema, timestampsSchema, uuidSchema } from "./common.ts";
import { participantSchema } from "./identity.ts";

export const bookingSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    sessionId: uuidSchema,
    participantId: uuidSchema,
    userId: uuidSchema,
    source: bookingSourceSchema,
    enrollmentId: uuidSchema.nullable(),
    status: bookingStatusSchema,
    creditEntryId: uuidSchema.nullable(),
    rescheduledToBookingId: uuidSchema.nullable(),
    rescheduleCount: z.number().int(),
    cancelledAt: isoDateTimeSchema.nullable(),
    /** The engine's answer at the moment the member confirmed, kept for support disputes. */
    policyOutcome: policyOutcomeSchema.nullable(),
  })
  .merge(timestampsSchema);
export type Booking = z.infer<typeof bookingSchema>;

/** Policy summary, so the app can render "free until Tue 17:00" without a second call. */
export const policySummarySchema = z.object({
  cancelCutoffHours: z.number().int(),
  lateCancelRule: z.enum(["lose_credit", "return_credit", "partial"]),
  lateCancelPartialBps: z.number().int().nullable(),
  rescheduleLimitPerBooking: z.number().int().nullable(),
  rescheduleLimitPerMonth: z.number().int().nullable(),
});
export type PolicySummary = z.infer<typeof policySummarySchema>;

/** The session as the schedule screen needs it, inlined on the booking. */
export const scheduleSessionSchema = z.object({
  id: uuidSchema,
  groupId: uuidSchema,
  groupName: z.string(),
  /** The session's own name ("Core awake") and what it is, from the timetable. Null: show the class name. */
  name: z.string().nullable().optional(),
  detail: z.string().nullable().optional(),
  groupColor: z.string().nullable(),
  /**
   * "recurring" is a fixed weekly slot the business placed the participant in. The app hides the
   * reschedule action for those, because the API refuses to move them (decision D12).
   */
  bookingModel: bookingModelSchema,
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  status: z.enum(["scheduled", "cancelled", "moved"]),
  capacity: z.number().int().nullable(),
});
export type ScheduleSession = z.infer<typeof scheduleSessionSchema>;

/**
 * The free cancellations of the purchase that paid for this booking, as they stand before this
 * cancellation. Read on the server through the booking's own credit entry, the same path the cancel
 * itself takes, so the screen never shows another purchase's terms.
 */
export const bookingAllowanceSchema = z.object({
  allowed: z.number().int().min(0),
  used: z.number().int().min(0),
  mode: freeCancellationModeSchema,
});
export type BookingAllowance = z.infer<typeof bookingAllowanceSchema>;

/** A booking with the session, the business, and the policy inlined. One request paints a day. */
export const scheduleEntrySchema = bookingSchema.extend({
  businessName: z.string(),
  session: scheduleSessionSchema,
  policy: policySummarySchema.nullable(),
  /**
   * The free cancellations of the purchase that paid for this booking, or null when it has no limit,
   * so the app can say what a cancel does before the member asks. Optional so older clients keep
   * parsing.
   */
  cancelAllowance: bookingAllowanceSchema.nullable().optional(),
});
export type ScheduleEntry = z.infer<typeof scheduleEntrySchema>;

export const scheduleQuerySchema = z.object({
  from: isoDateTimeSchema,
  to: isoDateTimeSchema,
  participantId: uuidSchema.optional(),
  businessId: uuidSchema.optional(),
});
export type ScheduleQuery = z.infer<typeof scheduleQuerySchema>;

/** Grouped by participant, because a member with three children thinks in children, not in rows. */
export const scheduleGroupSchema = z.object({
  participant: participantSchema,
  bookings: z.array(scheduleEntrySchema),
});
export type ScheduleGroup = z.infer<typeof scheduleGroupSchema>;

/** `GET /v1/me/schedule` returns the groups as a bare array. */
export const scheduleResponseSchema = z.array(scheduleGroupSchema);
export type ScheduleResponse = z.infer<typeof scheduleResponseSchema>;

/** One session for one person: a line of a batch, a hold, a waiting list join. */
export const bookingLineSchema = z.object({
  sessionId: uuidSchema,
  participantId: uuidSchema,
});

/**
 * Where a booking started (Studio Home H2), for `booking_created`: `open_spots` (the owner's open
 * spots message), `book_again`, `book_tab` (the Book tab), `month` (the month booking), `waitlist`.
 * Analytics only: it changes nothing in the booking.
 */
export const bookingEntries = ["open_spots", "book_again", "book_tab", "month", "waitlist"] as const;
export const bookingEntrySchema = z.enum(bookingEntries);
export type BookingEntry = z.infer<typeof bookingEntrySchema>;

/** `POST /v1/businesses/:businessId/bookings` */
export const createBookingRequestSchema = bookingLineSchema.extend({
  /** Where the member started (analytics only). Optional: older apps send none. */
  entry: bookingEntrySchema.optional(),
});
export type CreateBookingRequest = z.infer<typeof createBookingRequestSchema>;

export const createBookingResponseSchema = z.object({
  booking: bookingSchema,
  creditsRemaining: z.number().int(),
});
export type CreateBookingResponse = z.infer<typeof createBookingResponseSchema>;

/**
 * What a booking would cost, without booking: the package the server would spend, its free
 * cancellations, and the session's cancel rules. Same checks and same package choice as a booking.
 */
export const bookingPreviewResponseSchema = z.object({
  purchaseId: uuidSchema,
  /** The package's free cancellations, or null when it has no limit. */
  cancelAllowance: bookingAllowanceSchema.nullable(),
  cancelPolicy: z.object({
    cancelCutoffHours: z.number().int(),
    lateCancelRule: z.enum(["lose_credit", "return_credit", "partial"]),
    lateCancelPartialBps: z.number().int().nullable(),
  }),
});
export type BookingPreviewResponse = z.infer<typeof bookingPreviewResponseSchema>;

/** The most sessions one batch request books (`MAX_BOOKINGS_PER_REQUEST` on the server). */
export const MAX_BOOKINGS_PER_REQUEST = 60;

/**
 * `POST /v1/businesses/:id/bookings/batch`: the month booking. Every line is booked, or none is.
 */
export const createBookingsRequestSchema = z.object({
  lines: z
    .array(
      bookingLineSchema.extend({
        /**
         * The start the member saw when they picked it. Required: a session that moved since is
         * refused (`SESSION_CHANGED`), so nobody spends a session on a time they did not pick
         * (internal review F5.1, F6.2).
         */
        startsAt: isoDateTimeSchema,
      }),
    )
    .min(1)
    .max(MAX_BOOKINGS_PER_REQUEST),
  /** Where the member started (analytics only). Optional: older apps send none. */
  entry: bookingEntrySchema.optional(),
});
export type CreateBookingsRequest = z.infer<typeof createBookingsRequestSchema>;

export const createBookingsResponseSchema = z.object({
  /** In the order of the request: each booking and what a later cancel of it does. */
  items: z.array(z.object({ booking: bookingSchema, preview: bookingPreviewResponseSchema })),
  /** The sessions left of the family of the first line, after the bookings. */
  creditsRemaining: z.number().int(),
});
export type CreateBookingsResponse = z.infer<typeof createBookingsResponseSchema>;

/** How long a picked session place is held (decided 2026-10-08). Each new pick starts it again. */
export const SESSION_HOLD_MINUTES = 10;

/**
 * `PUT /v1/businesses/:id/holds`: every session place the member picked and has not confirmed yet.
 * The lines replace the member's holds at the business; an empty list lets them all go.
 */
export const setHoldsRequestSchema = z.object({
  lines: z.array(bookingLineSchema).max(MAX_BOOKINGS_PER_REQUEST),
});
export type SetHoldsRequest = z.infer<typeof setHoldsRequestSchema>;

export const setHoldsResponseSchema = z.object({
  /** When the holds end. Null when nothing is held. */
  expiresAt: isoDateTimeSchema.nullable(),
  held: z.array(bookingLineSchema),
  /** Times the server could not hold, with the code a booking of them would give. */
  lost: z.array(bookingLineSchema.extend({ code: z.string(), detail: z.string() })),
});
export type SetHoldsResponse = z.infer<typeof setHoldsResponseSchema>;

export * from "../waitlist-rules.ts";

/** `POST /v1/businesses/:id/waitlist`: join the waiting lists of full sessions, one line a session and person. */
export const joinWaitlistRequestSchema = z.object({
  lines: z.array(bookingLineSchema).min(1).max(MAX_BOOKINGS_PER_REQUEST),
});
export type JoinWaitlistRequest = z.infer<typeof joinWaitlistRequestSchema>;

/**
 * Where a place in line is, for the member (B2): `waiting` in line; `offered`, a place is free for the
 * person until `offerExpiresAt`; `offer_expired`, the time to accept ended and the offer went on.
 */
export const waitlistEntryStates = ["waiting", "offered", "offer_expired"] as const;
export const waitlistEntryStateSchema = z.enum(waitlistEntryStates);
export type WaitlistEntryState = z.infer<typeof waitlistEntryStateSchema>;

/** A person's place in line for a full session. */
export const waitlistEntrySchema = z.object({
  id: uuidSchema,
  sessionId: uuidSchema,
  participantId: uuidSchema,
  /** 1 for the first in line. An offer is 1. */
  position: z.number().int(),
  /** Optional for older API builds: a missing value means `waiting`. */
  status: waitlistEntryStateSchema.optional(),
  /** The end of the time to accept an offer. Null while the person waits. */
  offerExpiresAt: isoDateTimeSchema.nullable().optional(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  /** The session's name, or its class name. */
  title: z.string(),
  staffName: z.string().nullable(),
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const joinWaitlistResponseSchema = z.object({
  joined: z.array(waitlistEntrySchema),
  /** Lines the server did not put on a list, with the reason: a free place, the list closed, booked. */
  refused: z.array(bookingLineSchema.extend({ code: z.string(), detail: z.string() })),
});
export type JoinWaitlistResponse = z.infer<typeof joinWaitlistResponseSchema>;

/** `GET /v1/businesses/:id/waitlist`: every place in line of the member's family at the business. */
export const waitlistResponseSchema = z.object({ items: z.array(waitlistEntrySchema) });
export type WaitlistResponse = z.infer<typeof waitlistResponseSchema>;


/**
 * Book again (B1, client home where clients book for themselves). The classes of the client's
 * confirmed bookings of the last `BOOK_AGAIN_WEEKS` weeks (and the ones booked ahead) count, the
 * `BOOK_AGAIN_CLASSES` classes with the most bookings come first, and each class shows its next
 * `BOOK_AGAIN_TIMES` free times.
 */
export const BOOK_AGAIN_WEEKS = 8;
export const BOOK_AGAIN_CLASSES = 2;
export const BOOK_AGAIN_TIMES = 2;

/** One time of a usual class that the client can book now, or join the waiting list of. */
export const bookAgainItemSchema = z.object({
  sessionId: uuidSchema,
  groupId: uuidSchema,
  participantId: uuidSchema,
  /** The session's name, or its class name. */
  title: z.string(),
  /** The class colour, for the row's mark. */
  groupColor: z.string().nullable(),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  staffName: z.string().nullable(),
  /** `book`: a free place. `waitlist`: the session is full and its waiting list is open. */
  state: z.enum(["book", "waitlist"]),
  /** Free places. Null: the session has no limit. */
  spotsLeft: z.number().int().nullable(),
  /** The client's place in line on a full session (1 for the first). Null: not on the list. */
  waitlistPosition: z.number().int().nullable(),
});
export type BookAgainItem = z.infer<typeof bookAgainItemSchema>;

/** `GET /v1/me/businesses/:businessId/book-again` (also `/v1/me/clubs/:id/book-again`). */
export const bookAgainResponseSchema = z.object({
  /** The client's own participant here. Null: the account has none (nothing to book again). */
  participantId: uuidSchema.nullable(),
  /** The client's usual classes, most booked first. */
  classes: z.array(z.object({ groupId: uuidSchema, name: z.string(), bookings: z.number().int() })),
  /** The next times of those classes: by class (most booked first), then by time. */
  items: z.array(bookAgainItemSchema),
  /** The usual classes have times, but no package of the client can pay for them: buy a package first. */
  needsPackage: z.boolean(),
});
export type BookAgainResponse = z.infer<typeof bookAgainResponseSchema>;

/** One line of a `BOOKINGS_REFUSED` error's `meta.refused`. */
export const refusedBookingLineSchema = z.object({
  index: z.number().int(),
  sessionId: uuidSchema,
  participantId: uuidSchema,
  code: z.string(),
  detail: z.string(),
});
export type RefusedBookingLine = z.infer<typeof refusedBookingLineSchema>;

/**
 * `POST /v1/bookings/:id/cancel`.
 *
 * A dry run returns `outcome` and nothing else: no `booking` key at all, so the app can tell a
 * preview from a real cancellation by shape rather than by a flag it might forget to read.
 */


export const cancelBookingResponseSchema = z.object({
  outcome: policyOutcomeSchema,
  /**
   * On a dry run: the allowance behind this booking, or null when its package has no limit (or
   * there is no package). Absent from older servers and from a real cancellation.
   */
  allowance: bookingAllowanceSchema.nullable().optional(),
  /** The cancelled booking. Absent on a dry run. */
  booking: bookingSchema.optional(),
  creditsRemaining: z.number().int().optional(),
});
export type CancelBookingResponse = z.infer<typeof cancelBookingResponseSchema>;

export const rescheduleBookingRequestSchema = z.object({
  toSessionId: uuidSchema,
});
export type RescheduleBookingRequest = z.infer<typeof rescheduleBookingRequestSchema>;

export const rescheduleBookingResponseSchema = z.object({
  outcome: policyOutcomeSchema,
  /**
   * True when the move happens after the cut-off and the new booking therefore costs a credit.
   * The confirmation sheet says this out loud before the member commits.
   */
  costsAnotherCredit: z.boolean(),
  /** The original booking, now marked rescheduled. Absent on a dry run. */
  booking: bookingSchema.optional(),
  /** The booking on the new session. Absent on a dry run. */
  newBooking: bookingSchema.optional(),
  creditsRemaining: z.number().int().optional(),
});
export type RescheduleBookingResponse = z.infer<typeof rescheduleBookingResponseSchema>;
